import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Upload, Image as ImageIcon } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import type { FileObject } from '@shared/schema';

interface ImageLibraryProps {
  projectId: string;
}

export function ImageLibrary({ projectId }: ImageLibraryProps) {
  const [images, setImages] = useState<FileObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Image fetching disabled - image select function will be implemented later
  // useEffect(() => {
  //   if (!projectId) return;

  //   setLoading(true);
  //   setError(null);
  //   apiRequest('GET', `/api/projects/${projectId}/files`)
  //     .then((res) => res.json())
  //     .then((files: FileObject[]) => {
  //       // Filter for image files
  //       const imageFiles = files.filter((file) => {
  //         const path = file.objectPath || '';
  //         const ext = path.toLowerCase().split('.').pop() || '';
  //         return ['png', 'jpg', 'jpeg', 'svg', 'tiff', 'tif', 'gif', 'webp'].includes(ext);
  //       });
  //       setImages(imageFiles);
  //       setLoading(false);
  //     })
  //     .catch((err) => {
  //       console.error('Failed to load images:', err);
  //       setError('Failed to load images');
  //       setLoading(false);
  //     });
  // }, [projectId]);

  // Upload functionality disabled - coming soon
  // const handleFileUploaded = () => {
  //   setLoading(true);
  //   apiRequest('GET', `/api/projects/${projectId}/files`)
  //     .then((res) => res.json())
  //     .then((files: FileObject[]) => {
  //       const imageFiles = files.filter((file) => {
  //         const path = file.objectPath || '';
  //         const ext = path.toLowerCase().split('.').pop() || '';
  //         return ['png', 'jpg', 'jpeg', 'svg', 'tiff', 'tif', 'gif', 'webp'].includes(ext);
  //       });
  //       setImages(imageFiles);
  //       setLoading(false);
  //     })
  //     .catch((err) => {
  //       console.error('Failed to refresh images:', err);
  //       setLoading(false);
  //     });
  // };

  // Drag handlers disabled - image panels coming soon
  // const handleDragStart = (e: React.DragEvent, image: FileObject) => {
  //   e.dataTransfer.effectAllowed = 'move';
  //   e.dataTransfer.setData('application/json', JSON.stringify({
  //     type: 'image',
  //     imageId: image.id,
  //     imagePath: image.objectPath,
  //   }));
  //   const target = e.currentTarget as HTMLElement;
  //   if (target) {
  //     target.style.opacity = '0.5';
  //   }
  // };

  // const handleDragEnd = (e: React.DragEvent) => {
  //   const target = e.currentTarget as HTMLElement;
  //   if (target) {
  //     target.style.opacity = '1';
  //   }
  // };

  const getImageName = (file: FileObject) => {
    const meta = (file.metadata as Record<string, any> | null | undefined) ?? {};
    return meta.originalName || meta.filename || file.objectPath || `Image ${file.id}`;
  };

  return (
    <div className="h-full flex flex-col p-3">
      {/* Upload Button - Disabled with "Soon" badge */}
      <div className="mb-3">
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {}}
            disabled
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Image
          </Button>
          <Badge 
            variant="secondary" 
            className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0"
          >
            Soon
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="text-sm text-destructive py-4 px-2">{error}</div>
        )}

        {!loading && !error && images.length === 0 && (
          <div className="text-sm text-muted-foreground py-8 px-2 text-center">
            Image panels coming soon. Upload and assign images to panels will be available in a future update.
          </div>
        )}

        {!loading && !error && images.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {images.map((image) => {
              const imageName = getImageName(image);
              // Try to get a preview URL (this would need to be implemented based on your storage setup)
              const previewUrl = image.objectPath ? `/api/files/${image.id}/preview` : null;
              
              return (
                <Card
                  key={image.id}
                  draggable={false}
                  className="p-2 cursor-not-allowed opacity-60 group"
                  data-testid={`image-item-${image.id}`}
                >
                  <div className="flex flex-col gap-1">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={imageName}
                        className="w-full h-16 object-cover rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-16 bg-muted rounded flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex items-start gap-1">
                      <div className="text-xs font-medium truncate flex-1 text-muted-foreground">{imageName}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

